import { transcriptApi } from '../../../api/transcriptApi';
import { groupBlocksBySpeaker } from './speakerBlocks';

const SEGMENT_PAGE_SIZE = 500;
const MAX_SEGMENTS = 20000;
const PARALLEL_PAGE_REQUESTS = 6;

/** One page of segments, or null when the request fails. */
const fetchSegmentPage = (sessionId, offset, limit, lang) =>
  transcriptApi.getTranscriptSegments(sessionId, { offset, limit }, lang)
    .then((page) => (Array.isArray(page?.segments) ? page : null))
    .catch(() => null);

/**
 * Every segment through the paged endpoint, or null when it is unavailable.
 * Pages after the first load a few at a time; a failed page ends the list
 * there, so it is never out of order or missing a middle part.
 */
async function fetchAllSegments(sessionId, lang) {
  const first = await fetchSegmentPage(sessionId, 0, SEGMENT_PAGE_SIZE, lang);
  if (!first) return null;

  const total = Math.min(first.total ?? first.segments.length, MAX_SEGMENTS);
  const limit = first.limit || SEGMENT_PAGE_SIZE;
  const offsets = [];
  for (let offset = (first.offset ?? 0) + limit; offset < total; offset += limit) offsets.push(offset);
  if (offsets.length === 0) return first.segments;

  const pages = new Array(offsets.length);
  let nextPage = 0;
  const loadPages = async () => {
    while (nextPage < offsets.length) {
      const index = nextPage++;
      const page = await fetchSegmentPage(sessionId, offsets[index], limit, lang);
      pages[index] = page ? page.segments : null;
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL_PAGE_REQUESTS, offsets.length) }, loadPages));

  const segments = [...first.segments];
  for (const pageSegments of pages) {
    if (!pageSegments) break;
    segments.push(...pageSegments);
    if (segments.length >= MAX_SEGMENTS) break;
  }
  return segments;
}

const segmentToBlock = (segment, index) => ({
  // Segment id 0 is valid, so `||` must not replace it: the id has to match
  // the server id for word timings and saves.
  id: segment.id != null ? String(segment.id) : `segment-${index}`,
  text: segment.text || '',
  speaker: segment.speaker || 'UNKNOWN',
  start: segment.start || 0,
  end: segment.end || 0,
  words: segment.words || [],
  paragraphNumber: segment.paragraph_number || index,
  metadata: {
    isEdited: segment.is_edited || false,
    lastModified: segment.last_modified || new Date().toISOString(),
    version: segment.version || 1,
  },
});

/**
 * The transcript as editor blocks grouped by speaker: through the paged segments
 * endpoint, or the whole transcript when paging is unavailable. Empty when the
 * session has no transcript yet.
 */
export async function loadTranscriptBlocks(sessionId, lang) {
  try {
    const segments = (await fetchAllSegments(sessionId, lang))
      ?? (await transcriptApi.getTranscript(sessionId, lang)).segments;
    return groupBlocksBySpeaker((segments || []).map(segmentToBlock));
  } catch {
    return [];
  }
}
